import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowUpRight, RefreshCw, LockKeyhole } from 'lucide-react'
import OrderSearchBar from './OrderSearchBar'
import { TrackingDetailsState } from '@/hooks/useOrderTracking'
import './TrackingPageLayout.css'
import './TrackingRequestState.css'

const waitingPhoto = '/assets/images/Boy-and-Father-Listening-Together.jpg'

export function TrackingLoadingState({ orderNumber }: { orderNumber?: string }) {
 const [takingLonger, setTakingLonger] = useState(false)
 useEffect(() => {
  setTakingLonger(false)
  const timer = setTimeout(() => setTakingLonger(true), 6000)
  return () => clearTimeout(timer)
 }, [orderNumber])
 return <main className="tracking-experience tracking-skeleton-page" aria-busy="true" aria-label="Loading order tracking">
  <div className="tracking-content">
   <div className="tracking-order-heading"><Link to="/track">Your Kibu journey <ArrowUpRight size={14} /></Link>{orderNumber && <span>{orderNumber}</span>}</div>
   <p className="tracking-loading-announcement" role="status">{takingLonger ? 'Still loading your order. This is taking longer than usual.' : 'Loading your order tracking.'}</p>
   <div className="tracking-skeleton-panel" aria-hidden="true">
    <div className="tracking-skeleton-hero">
     <div className="tracking-skeleton-kicker"><span className="tracking-skeleton-fill" /><span className="tracking-skeleton-fill" /></div>
     <div className="tracking-skeleton-title"><span className="tracking-skeleton-fill" /><span className="tracking-skeleton-fill" /></div>
     <div className="tracking-skeleton-description"><span className="tracking-skeleton-fill" /><span className="tracking-skeleton-fill" /></div>
     <div className="tracking-skeleton-next"><span className="tracking-skeleton-fill" /><span className="tracking-skeleton-fill" /></div>
    </div>
    <div className="tracking-skeleton-media tracking-skeleton-fill" />
    <div className="tracking-skeleton-journey">{[0, 1, 2, 3].map(index => <div key={index}><i className="tracking-skeleton-fill" /><span><b className="tracking-skeleton-fill" /><b className="tracking-skeleton-fill" /></span></div>)}</div>
   </div>
   <div className="tracking-skeleton-details" aria-hidden="true"><span className="tracking-skeleton-fill" /><span className="tracking-skeleton-fill" /></div>
  </div>
 </main>
}

export function TrackingErrorState({ orderNumber, notFound, message, onRetry }: { orderNumber: string; notFound: boolean; message: string; onRetry: () => void }) {
 const navigate = useNavigate()
 return <main className="tracking-experience tracking-request-page" aria-labelledby="tracking-error-title">
  <div className="tracking-content">
   <div className="tracking-order-heading"><Link to="/track">Your Kibu journey <ArrowUpRight size={14} /></Link><span>{orderNumber}</span></div>
   <section className="tracking-waiting-panel tracking-error-panel">
    <div className="tracking-waiting-copy">
     <span className="tracking-eyebrow">Order tracking</span>
     <h1 id="tracking-error-title">{notFound ? 'Let’s find your order.' : 'Your update couldn’t load.'}</h1>
     <p role="alert">{message}</p>
     {!notFound && <button type="button" className="tracking-request-retry" onClick={onRetry}><RefreshCw size={16} />Try again</button>}
     <div className="tracking-error-search"><label htmlFor="tracking-order-number">{notFound ? 'Check your order number' : 'Or find a different order'}</label><OrderSearchBar key={orderNumber} initialValue={orderNumber} onSearch={number => { if (number === orderNumber) onRetry(); else navigate(`/track/${number}`) }} /></div>
     <p className="tracking-error-hint">You’ll find your order number in your confirmation email.</p>
     <a className="tracking-request-help" href="mailto:hello@kibu.family">Need a hand? Contact the Kibu team <ArrowUpRight size={15} /></a>
    </div>
    <figure className="tracking-waiting-photo"><img src={waitingPhoto} alt="A father and son listening together with Kibu headphones" /></figure>
   </section>
  </div>
 </main>
}

export function TrackingPrivateDetails({ state = 'not-requested', onRetry, children }: { state?: TrackingDetailsState; onRetry?: () => void; children: React.ReactNode }) {
 if (state === 'loading') return <section className="tracking-private-skeleton" aria-busy="true" aria-label="Order details">
  <h2 className="tracking-loading-announcement">Loading your order details.</h2>
  <p className="tracking-loading-announcement" role="status">Your order details are loading. Your order journey is available.</p>
  <div aria-hidden="true"><i className="tracking-skeleton-fill" /><span><b className="tracking-skeleton-fill" /><b className="tracking-skeleton-fill" /></span></div>
 </section>
 if (state === 'unavailable') return <section className="tracking-details-notice" aria-label="Order details">
  <LockKeyhole size={22} aria-hidden="true" />
  <div><h2>Your order details couldn’t load.</h2><p role="status">Your order update is still available. Try again to see your items and delivery details.</p></div>
  {state === 'unavailable' && <button type="button" onClick={onRetry}><RefreshCw size={15} />Try order details again</button>}
 </section>
 return <>{state === 'expired' && <p className="tracking-link-expired" role="status">This secure link is no longer valid. Request a fresh link below to see your order details.</p>}{children}</>
}
